import styles from './index.module.scss';
import { fabric } from 'fabric';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useContext } from 'react';
import useCanvasZoom from '../hooks/useCanvasZoom';
import useCanvasMovement from '../hooks/useCanvasMovement';
import { RESIZE_IMG_CANVASNAME } from '@utils/CONST'
import Utils from '@utils/utilsEdit';
import { ImageResizeContext } from '../../imageResizeContext'
import API from "@api/api";
import { IMAGE_TYPE } from '@utils/CONST'

const CanvasRightComFill = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        setPosition: (position) => {
            if (canvasInstance.current) {
                canvasInstance.current.viewportTransform[4] += position.left
                canvasInstance.current.viewportTransform[5] += position.top
                rectRef.current.setCoords()
                canvasInstance.current.requestRenderAll();
            }
        },
        exportImageContainMode
    }))

    const { imageUrl, onZoom, zoom, initMinZoom, rectWidth, rectHeight, onMove } = props
    const canvasRef = useRef(null);
    const canvasInstance = useRef(null);
    const [imgWidth, setImgWidth] = useState()
    const [imgHeight, setImgHeight] = useState()
    const rectRef = useRef()
    const isMounted = useRef()
    const bgPattern = useRef()
    const {
        targetSsize,
        setDownBtnLoading,
        saveResultToProject,
        AIEditTemTaskId
    } = useContext(ImageResizeContext)


    useEffect(() => {
        const patternImageBase64 = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8
        /9hAAAABHNCSVQICAgIfAhkiAAAADBJREFUOE9jfPP69X8GPEBYRASfNAPjqAHDIgz+AwG+iH7z5s1ZfPKMowYwDIMwAACuR1xZvSaHFwAAAABJRU5ErkJggg==`;
        const pattern = new fabric.Pattern({
            source: patternImageBase64,
            repeat: 'repeat',
            patternTransform: [
                3, 0,
                0, 3,
                0, 0
            ]
        });
        bgPattern.current = pattern
    }, [])
    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])




    const initCanvas = () => {
        if (!canvasRef.current?.parentElement) return;
        const container = canvasRef.current.parentElement;
        const { width, height } = container.getBoundingClientRect();

        if (canvasInstance.current) {
            canvasInstance.current.dispose();
        }

        canvasInstance.current = new fabric.Canvas(canvasRef.current, {
            width,
            height,
            backgroundColor: 'transparent',
            selectable: false,
            defaultCursor: 'grab',
            hoverCursor: 'grab',
            moveCursor: 'grabbing',
        })
    };

    const loadImage = (url) => {
        if (!canvasInstance.current || !canvasRef.current?.parentElement) return;
        fabric.Image.fromURL(url, (img) => {
            if (!canvasRef.current?.parentElement || !canvasInstance.current) return;
            const container = canvasRef.current.parentElement;
            const originalWidth = img.width;
            const originalHeight = img.height;

            setImgWidth(originalWidth)
            setImgHeight(originalHeight)
            // 计算基于容器短边的缩放比例
            const containerRatio = container.clientWidth / container.clientHeight;
            const imageRatio = rectWidth / rectHeight;
            let scale = 1;
            if (containerRatio > imageRatio) {
                scale = container.clientHeight / rectHeight;
            } else {
                scale = container.clientWidth / rectWidth;
            }
            // 第一次先获取缩放倍数，传导父组件比较后再往下渲染
            onZoom(scale, RESIZE_IMG_CANVASNAME.RIGHT_CANVAS_NAME)
            if (!initMinZoom) return

            const leftRectPoint = (container.clientWidth - rectWidth * initMinZoom) / 2
            const topRectPoint = (container.clientHeight - rectHeight * initMinZoom) / 2


            const rect = new fabric.Rect({
                left: leftRectPoint,
                top: topRectPoint,
                width: rectWidth,
                height: rectHeight,
                fill: bgPattern.current,
                selectable: false,
                hasControls: false,
                lockRotation: true,
                type: 'rect',
                hoverCursor: 'grab',
                moveCursor: 'grabbing',
            });
            canvasInstance.current.add(rect);
            rectRef.current = rect

            // 插入图片
            let imageScale = 1;
            const rectRatio = rect.width / rect.height;
            if (rect.width < rect.height) {
                imageScale = rect.width / originalWidth;
            } else {
                imageScale = rect.height / originalHeight;
            }
            const displayWidth = originalWidth * imageScale;
            const displayHeight = originalHeight * imageScale;
            const imageLeft = rect.left + (rect.width - displayWidth) / 2;
            const imageTop = rect.top + (rect.height - displayHeight) / 2;


            img.set({
                left: imageLeft,
                top: imageTop,
                scaleX: imageScale,
                scaleY: imageScale,
                selectable: false,
                evented: false,
                hasControls: false,
                hasBorders: false,
                lockMovementX: true,
                lockMovementY: true,
                lockRotation: true,
                lockScalingX: true,
                lockScalingY: true,
                hoverCursor: 'grab',
                moveCursor: 'grabbing',
                type: 'image',
            });
            canvasInstance.current.add(img);

            canvasInstance.current.zoomToPoint({ x: leftRectPoint, y: topRectPoint }, initMinZoom);
            canvasInstance.current.renderAll();
        });
    };

    // 处理窗口大小变化
    useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            if (!canvasInstance.current) return;
            const { width, height } = entries[0].contentRect;
            canvasInstance.current.setDimensions({ width, height });
            canvasInstance.current.requestRenderAll();
        });

        const parentDiv = canvasRef.current?.parentElement;
        if (parentDiv) {
            resizeObserver.observe(parentDiv);
        }
    }, []);

    useEffect(() => {
        initCanvas();
        loadImage(imageUrl);
    }, [imageUrl, initMinZoom]);

    // 处理缩放
    useCanvasZoom(
        canvasInstance,
        null,
        zoom,
        onZoom,
        initMinZoom,
        RESIZE_IMG_CANVASNAME.SCROLL_CANVAS
    )

    // 移动画布
    useCanvasMovement(canvasInstance, ({ left, top }) => {
        if (onMove) onMove({ left, top })
    });


    const exportImageContainMode = async (type) => {
        setDownBtnLoading(true)
        const database64 = await Utils.exportImageContainMode(imageUrl, rectWidth, rectHeight, type, `PSAI-${Date.now()}`)
        const dataUrl = await Utils.uploadBase64Image(database64, true)
        const res2 = await saveResultToProject(imageUrl,dataUrl, '', IMAGE_TYPE.IMAGE_RESIZE, AIEditTemTaskId)
        if (!res2) return
        setDownBtnLoading(false)
    }


    return (
        <div className={styles.canvasRightComFill}>
            <span className={styles.sizeTips}>结果图：{Math.min(targetSsize[1], 8192)} * {Math.min(targetSsize[0], 8192)}px</span>
            <canvas ref={canvasRef} />
        </div>
    );
})
export default CanvasRightComFill;
